import { getResolutionInfo } from '@mm/hooks';
import { ErrorReportingUtils } from '@mm/plugins';
import attributeCRUDActions from '@mm/state/actions/AttributeCRUDActions';
import {
  activatePageEditor,
  selectMainMenu,
} from '@mm/state/actions/viewActions';
import WidgetCRUDActions from '@mm/state/actions/WidgetCRUDActions';
import { HiddenPageKinds } from '@mm/state/model/PageModel';
import { defaultPageSelector } from '@mm/state/selectors/onboardingSelectors';
import {
  activeChatbotEditorPageSelector,
  connectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import scrollIntoView from 'dom-scroll-into-view';
import { get } from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { PrimarySelectWithPush } from '../../../../../common/components/PrimarySelect/PrimarySelectWithPush/PrimarySelectWithPush';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import { getDeviceInfo } from '../../../../../common/hooks/use-device-info';
import { SIDEBAR_MENU } from '../../../../../layout/BotBuilderSidebar/sidebar-menu';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import MenuEditorPane from '../../components/MenuEditorPane/MenuEditorPane';
import PageEditorPane from '../../components/PageEditorPane/PageEditorPane';
import { PageOverviewPane } from '../../components/PageOverviewPane/PageOverviewPane';
import './ChatbotBuildPanel.pcss';

type OuterProps = RouterComponentProps;

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = InnerProps & OuterProps;

type State = {
  scrollRequest?: number;
  dialoguesPaneOpen: boolean;
};

class ChatbotBuildPanel extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      scrollRequest: undefined,
      dialoguesPaneOpen: true,
    };
  }

  componentDidMount() {
    const {
      match,
      activatePageEditor,
      selectMainMenu,
      pages,
      history,
      defaultPage,
    } = this.props;
    const { pageId: pageIdStr, chatbotId: chatbotIdStr } = match.params;
    const pageId = Number(pageIdStr);

    const { isMobile } = getDeviceInfo();

    if (pageId) {
      activatePageEditor(pageId);
      this.setState({ scrollRequest: pageId });
      if (isMobile) {
        this.setState({ dialoguesPaneOpen: false });
      }
    } else {
      if (match.url.indexOf('main-menu') > -1) {
        if (isMobile) {
          this.setState({ dialoguesPaneOpen: false });
        }
        selectMainMenu();
      } else {
        const pagesWithoutWelcomeAndStartbots = pages.filter(
          page => !HiddenPageKinds.includes(page.kind),
        );

        const activePage = defaultPage || pagesWithoutWelcomeAndStartbots[0];

        if (activePage) {
          // The check prevents requesting a page of a different Chatbot
          if (activePage.botId === Number(chatbotIdStr)) {
            history.push(
              BotBuilderRoutes.build.getLink({
                chatbotId: activePage.botId,
                dialogueId: activePage.id,
              }),
            );

            activatePageEditor(activePage.id);
          }
        } else {
          ErrorReportingUtils.reportError('Bot missing default page', {
            extraData: {
              chatbotId: chatbotIdStr,
            },
          });
        }
      }
    }
  }

  componentDidUpdate(prevProps: Props) {
    const {
      getPageAttributes,
      connectedPage,
      page: nextPage,
      history,
      retrieveWidgets,
      match: nextMatch,
      activatePageEditor,
      mainMenuSelected,
    } = this.props;
    const {
      page: prevPage,
      match: prevMatch,
      mainMenuSelected: prevMainMenuSelected,
    } = prevProps;
    if (connectedPage) {
      const connectedPageIsSet = !prevProps.connectedPage;
      const connectedPageIsChanged =
        prevProps.connectedPage &&
        connectedPage.id !== prevProps.connectedPage.id;
      if (connectedPageIsSet || connectedPageIsChanged) {
        getPageAttributes(connectedPage.id);
      }
    }

    const isSwitchFromMainMenu = prevMainMenuSelected && !mainMenuSelected;

    if (
      nextPage &&
      (!prevPage || prevPage.id !== nextPage.id || isSwitchFromMainMenu)
    ) {
      history.push(
        BotBuilderRoutes.build.getLink({
          chatbotId: nextPage.botId,
          dialogueId: nextPage.id,
        }),
      );
      retrieveWidgets(nextPage.id);
      this.setState({ scrollRequest: nextPage.id }); // eslint-disable-line react/no-did-update-set-state
    } else if (
      get(prevMatch, 'params.pageId', -1) !==
      get(nextMatch, 'params.pageId', -1)
    ) {
      const pageId =
        Number(nextMatch.params.pageId) || Number(prevMatch.params.pageId);
      if (pageId) {
        activatePageEditor(pageId);
      }
    }
  }

  pageBelongsToChatbot(pages, pageIdNumber) {
    const hasPages = pages && pages.length !== 0;

    if (hasPages) {
      return pages.some(page => page.id === pageIdNumber);
    }

    return false;
  }

  handleDialoguesPaneState = (isDialoguePaneOpen: boolean) => {
    this.setState({ dialoguesPaneOpen: isDialoguePaneOpen });
  };

  render() {
    const { mainMenuSelected, pagesBusy } = this.props;
    const { scrollRequest, dialoguesPaneOpen } = this.state;

    if (!pagesBusy && scrollRequest) {
      const pageId = scrollRequest;
      this.setState({ scrollRequest: undefined });
      setTimeout(() => {
        const target = document.querySelector(
          `li[data-bot-builder-page-id="${pageId}"]`,
        );
        const container = document.querySelector(
          `div.chatbot-build-panel__left-pane`,
        );
        if (target && container) {
          scrollIntoView(target, container, { onlyScrollIfNeeded: true });
        }
      }, 100);
    }

    const dialoguesPaneText = dialoguesPaneOpen
      ? 'Choose a dialogue'
      : 'Open another dialogue';

    // Note: it's not really a hook, thought named like it
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { isMobile } = getDeviceInfo();
    const { screenWidth } = getResolutionInfo();
    const menuOptions = SIDEBAR_MENU.filter(menu => menu.id === 'CHATBOT')[0]
      ?.menuItems;

    return (
      <>
        {isMobile && screenWidth < 600 && (
          <PrimarySelectWithPush
            options={menuOptions}
            value={menuOptions[1]}
            headerText="Bot Builder"
            customDropdownStyles={{ zIndex: '99' }}
          />
        )}
        <div className="chatbot-build-panel">
          <div className="chatbot-build-panel__body">
            {isMobile && (
              <div
                onClick={() =>
                  this.setState({ dialoguesPaneOpen: !dialoguesPaneOpen })
                }
                className="page-overview__view-dialogues-pane"
              >
                <span
                  className={classnames({
                    'page-overview__long-arrow-top': dialoguesPaneOpen,
                    'page-overview__long-arrow-bottom': !dialoguesPaneOpen,
                  })}
                />
                <span>{dialoguesPaneText}</span>
              </div>
            )}
            {dialoguesPaneOpen && (
              <div className="chatbot-build-panel__left-pane">
                <PageOverviewPane
                  handleDialoguesPaneState={this.handleDialoguesPaneState}
                  isMobile={isMobile}
                />
              </div>
            )}
            {isMobile ? (
              !dialoguesPaneOpen && (
                <div className="chatbot-build-panel__main-builder">
                  {mainMenuSelected ? <MenuEditorPane /> : <PageEditorPane />}
                </div>
              )
            ) : (
              <div className="chatbot-build-panel__main-builder">
                {mainMenuSelected ? <MenuEditorPane /> : <PageEditorPane />}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state: StoreState) => ({
  pages: state.page.entities,
  pagesBusy: state.page.busy,
  defaultPage: defaultPageSelector(state),
  page: activeChatbotEditorPageSelector(state),
  mainMenuSelected: state.view.chatbotEditor.mainMenuSelected,
  connectedPage: connectedPageSelector(state),
  activePageId: state.view.chatbotEditor.build.activePageId,
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>, ownerProps) => ({
  activatePageEditor: (pageId: number) => {
    const keepMainMenu =
      ownerProps.match &&
      ownerProps.match.url &&
      ownerProps.match.url.indexOf('main-menu') > -1;
    dispatch(activatePageEditor(pageId, keepMainMenu));
    dispatch(WidgetCRUDActions.retrieveAll(pageId));
  },
  retrieveWidgets: pageId => dispatch(WidgetCRUDActions.retrieveAll(pageId)),
  getPageAttributes: (pageId: number) =>
    dispatch(attributeCRUDActions.retrieveAll(pageId)),
  selectMainMenu: () => dispatch(selectMainMenu()),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(ChatbotBuildPanel);
